import React from 'react';

import { Grid, TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { Stack } from '@liscio/ui';

import { CreateAccountPrefTypeOptions, CreateAccountSectionProps } from './sectionTypes';
import { LabeledField, SubsectionTitle } from '../CreateAccountFormFieldPrimitives';
import { CreateAccountFormValues } from '../createAccountValidationSchema';
import { InlineRichOptionSelect } from '../InlineRichOptionSelect';
import { MONTH_RICH_OPTIONS } from '../inputs/monthOptions';
import { useHomeData } from 'fetch-utils/users/user-hooks';

export interface CreateAccountBusinessDetailsFieldsProps
  extends CreateAccountSectionProps {
  prefTypeOptions?: CreateAccountPrefTypeOptions;
}

export const CreateAccountBusinessDetailsFields: React.FC<
  CreateAccountBusinessDetailsFieldsProps
> = ({ layout = 'responsive', prefTypeOptions: prefTypeOptionsProp }) => {
  const { data: homeData } = useHomeData();
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<CreateAccountFormValues>();

  const prefTypeOptions =
    prefTypeOptionsProp ?? homeData?.data?.pref_type_options;
  const industryOptions = prefTypeOptions?.industry;
  const firmType = (homeData?.data as { firm_type?: string } | undefined)
    ?.firm_type;

  const pairedFieldBreakpoint = layout === 'single-column' ? 12 : 4;
  const expectationsLabel =
    firmType && firmType !== 'cpa'
      ? 'What would you like most from a legal firm?'
      : 'What would you like most from an accounting firm?';

  return (
    <Stack spacing={2}>
      <SubsectionTitle>Account Details</SubsectionTitle>
      <Grid container spacing={2.5} width="100%" sx={{ m: 0 }}>
        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Taxpayer ID">
            {/* Collected for future updateEncryptedField after account create */}
            <TextField
              {...register('encryptedCoEin')}
              fullWidth
              type="password"
              placeholder="XX-XXXXXXX"
              error={Boolean(errors.encryptedCoEin)}
              helperText={errors.encryptedCoEin?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        {industryOptions && (
          <Grid item xs={12} sm={pairedFieldBreakpoint}>
            <LabeledField label="Industry">
              <InlineRichOptionSelect
                name="co_industry"
                control={control}
                data={industryOptions}
                error={errors.co_industry}
                placeholder="Select..."
              />
            </LabeledField>
          </Grid>
        )}

        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Prior Accountant">
            <TextField
              {...register('co_prior_accountant')}
              fullWidth
              error={Boolean(errors.co_prior_accountant)}
              helperText={errors.co_prior_accountant?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="# of employees">
            <TextField
              {...register('co_number_of_employees')}
              fullWidth
              error={Boolean(errors.co_number_of_employees)}
              helperText={errors.co_number_of_employees?.message}
              autoComplete="off"
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12} sm={pairedFieldBreakpoint}>
          <LabeledField label="Fiscal Year End">
            <InlineRichOptionSelect
              name="co_fiscal_yearend"
              control={control}
              data={MONTH_RICH_OPTIONS}
              error={errors.co_fiscal_yearend}
              placeholder="Select..."
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12}>
          <LabeledField label="What are your biggest challenges?">
            <TextField
              {...register('co_biggest_challenges')}
              fullWidth
              multiline
              minRows={2}
              error={Boolean(errors.co_biggest_challenges)}
              helperText={errors.co_biggest_challenges?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12}>
          <LabeledField label="What do you use to make business decisions?">
            <TextField
              {...register('co_biz_decision_tools_used')}
              fullWidth
              multiline
              minRows={2}
              error={Boolean(errors.co_biz_decision_tools_used)}
              helperText={errors.co_biz_decision_tools_used?.message}
            />
          </LabeledField>
        </Grid>

        <Grid item xs={12}>
          <LabeledField label={expectationsLabel}>
            <TextField
              {...register('co_expectations')}
              fullWidth
              multiline
              minRows={2}
              error={Boolean(errors.co_expectations)}
              helperText={errors.co_expectations?.message}
            />
          </LabeledField>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default CreateAccountBusinessDetailsFields;
